import { useCallback, useRef, useState } from 'react';
import { Footer } from '../components/Footer.js';
import { Nav } from '../components/Nav.js';

export interface FaqItem {
  q: string;
  a: string;
}

export interface FaqCategory {
  id: string;
  n: string;
  title: string;
  desc: string;
  items: FaqItem[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'installation',
    n: '01',
    title: 'Installation',
    desc: 'Branchement, Wi-Fi, premier démarrage.',
    items: [
      {
        q: 'Combien de temps pour installer la tablette ?',
        a: "Environ 20 minutes pour quelqu'un qui n'a jamais fait ça. Brancher la tablette, la connecter au Wi-Fi depuis l'app, créer le foyer : c'est tout.",
      },
      {
        q: "Faut-il un technicien pour l'installation ?",
        a: 'Non. La tablette est préconfigurée. Si vous pouvez connecter une box à Internet, vous pouvez installer Cadran.',
      },
      {
        q: 'La tablette fonctionne-t-elle sans Wi-Fi ?',
        a: "Elle continue d'afficher l'heure, l'agenda déjà chargé et les alertes programmées. Les nouveaux messages ou photos attendent le retour réseau.",
      },
    ],
  },
  {
    id: 'usage-senior',
    n: '02',
    title: 'Usage côté senior',
    desc: 'Ce que voit et fait votre proche.',
    items: [
      {
        q: 'Mon parent doit-il apprendre quelque chose ?',
        a: "Non. L'écran s'allume comme une horloge. Il n'y a aucun menu, aucun login, aucune notification à gérer. Pour acquitter une alerte : toucher le gros bouton.",
      },
      {
        q: 'Que se passe-t-il quand un message arrive ?',
        a: "Un bandeau coloré apparaît entre l'horloge et l'agenda. Un gros bouton \"J'ai vu\" permet de l'acquitter. La famille est notifiée de la validation.",
      },
      {
        q: 'Et les photos ?',
        a: "Elles s'affichent en plein écran en diaporama lorsque l'écran est inactif depuis 5 minutes. Pas de son, pas d'action requise.",
      },
    ],
  },
  {
    id: 'famille',
    n: '03',
    title: "L'app famille",
    desc: "Envoyer un message, gérer l'agenda.",
    items: [
      {
        q: 'Comment inviter les autres membres de la famille ?',
        a: "Dans l'app, section Famille → Inviter un proche. Ils reçoivent un e-mail, s'inscrivent en 2 minutes et peuvent immédiatement envoyer un message.",
      },
      {
        q: 'Combien de proches peuvent rejoindre ?',
        a: "Autant que vous voulez. L'optimal est 1 à 3 par foyer pour éviter les doublons, mais il n'y a pas de limite technique.",
      },
      {
        q: "Puis-je envoyer des photos depuis l'app ?",
        a: "Oui, depuis l'onglet Photos. Elles sont ré-encodées côté serveur et apparaissent dans le diaporama du prochain cycle d'inactivité.",
      },
    ],
  },
  {
    id: 'securite',
    n: '04',
    title: 'Sécurité & données',
    desc: 'Où sont les données, qui y accède.',
    items: [
      {
        q: 'Mes données sont-elles dans le cloud ?',
        a: 'Non. Elles restent dans la tablette au domicile, chiffrées au repos (SQLCipher + clé liée au matériel). Tailscale ne voit que les IPs et le nom SNI.',
      },
      {
        q: 'Que se passe-t-il si je perds la tablette ?',
        a: "Les données sont inutilisables sans la carte SD couplée au matériel (clé HKDF dérivée du numéro de série CPU). Personne d'autre ne peut les lire.",
      },
      {
        q: 'Comment exporter ou supprimer mes données ?',
        a: "Dans l'app Réglages → Exporter (ZIP complet RGPD) ou Supprimer le foyer (cascade effective auditée). Vos droits RGPD sont accessibles en autonomie.",
      },
    ],
  },
  {
    id: 'tarifs',
    n: '05',
    title: 'Tarifs & commande',
    desc: 'Prix, formules, paiement.',
    items: [
      {
        q: 'Combien ça coûte ?',
        a: '249 € pour la tablette seule, sans abonnement. La formule Famille ajoute 10 €/mois pour le support prioritaire et les sauvegardes. Présence plus inclut une SIM 4G à 30 €/mois.',
      },
      {
        q: "Puis-je tester avant d'acheter ?",
        a: 'Oui, 30 jours satisfait ou remboursé sans condition. Retournez la tablette en bon état et vous êtes remboursé intégralement.',
      },
      {
        q: 'Y a-t-il un engagement minimum ?',
        a: 'Aucun. Les abonnements mensuels sont résiliables à tout moment, immédiatement depuis votre espace.',
      },
    ],
  },
  {
    id: 'technique',
    n: '06',
    title: 'Questions techniques',
    desc: 'Compatibilité, pannes, mises à jour.',
    items: [
      {
        q: 'Quelle connexion Internet est nécessaire ?',
        a: "N'importe quelle box ADSL / fibre avec Wi-Fi 2,4 ou 5 GHz. La tablette consomme très peu de bande passante (texte + photos compressées).",
      },
      {
        q: 'Que se passe-t-il en cas de coupure secteur ?',
        a: "La tablette redémarre automatiquement à l'alimentation et reprend son état normal. Les alertes en cours sont récupérées depuis le serveur.",
      },
      {
        q: 'Comment se font les mises à jour ?',
        a: 'En arrière-plan, automatiquement la nuit (formules Famille et Présence plus). Pour la tablette seule, un script de mise à jour est fourni.',
      },
    ],
  },
];

export function doSearch(query: string, categories: FaqCategory[]): FaqCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;
  return categories
    .map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          item.q.toLowerCase().includes(q) ||
          item.a.toLowerCase().includes(q) ||
          cat.title.toLowerCase().includes(q),
      ),
    }))
    .filter((cat) => cat.items.length > 0);
}

export function Faq() {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const visible = doSearch(query, FAQ_CATEGORIES);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  return (
    <>
      <Nav active="faq" />
      <main>
        <div className="page-intro container">
          <div className="eyebrow">Aide & FAQ</div>
          <h1>
            Une question ? <em>Réponse rapide.</em>
          </h1>
          <p className="lede">
            Toutes les réponses aux questions que vous vous posez avant d'acheter.
          </p>
        </div>

        <div className="container" style={{ marginBottom: '40px' }}>
          <div
            style={{
              background: 'var(--paper)',
              border: '1px solid var(--hairline-soft)',
              borderRadius: '999px',
              padding: '14px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: 'var(--shadow-card)',
              maxWidth: '560px',
            }}
          >
            <span style={{ color: 'var(--ink-muted)', fontSize: '18px' }}>🔍</span>
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={handleSearch}
              placeholder="Rechercher dans la FAQ…"
              aria-label="Rechercher dans la FAQ"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '16px',
                color: 'var(--ink)',
                width: '100%',
                fontFamily: 'Inter Variable, sans-serif',
              }}
            />
          </div>
        </div>

        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: '240px 1fr',
            gap: '56px',
            paddingBottom: '96px',
            alignItems: 'start',
          }}
        >
          <nav style={{ position: 'sticky', top: '96px' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {FAQ_CATEGORIES.map((cat) => (
                <li key={cat.id} style={{ marginBottom: '8px' }}>
                  <a
                    href={`#${cat.id}`}
                    className="small"
                    style={{ display: 'block', padding: '6px 0', color: 'var(--ink-soft)' }}
                  >
                    {cat.n} — {cat.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            {visible.length === 0 && (
              <p style={{ color: 'var(--ink-muted)', fontStyle: 'italic' }}>
                Aucun résultat pour « {query} ».
              </p>
            )}
            {visible.map((cat) => (
              <section
                key={cat.id}
                id={cat.id}
                style={{ marginBottom: '64px', scrollMarginTop: '100px' }}
              >
                <div style={{ marginBottom: '24px' }}>
                  <div className="eyebrow">{cat.n}</div>
                  <h2
                    style={{
                      fontFamily: 'Fraunces Variable, serif',
                      fontWeight: 350,
                      fontSize: 'clamp(32px, 3vw, 44px)',
                      lineHeight: 0.98,
                      letterSpacing: '-0.02em',
                      margin: '10px 0 8px',
                    }}
                  >
                    {cat.title}
                  </h2>
                  <p className="small">{cat.desc}</p>
                </div>
                <div className="faq-list">
                  {cat.items.map((item) => (
                    <details key={item.q} className="faq-item">
                      <summary>
                        {item.q}
                        <span className="faq-plus" aria-hidden="true">
                          +
                        </span>
                      </summary>
                      <p>{item.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            ))}

            <div
              className="section ink"
              style={{
                borderRadius: '16px',
                padding: '48px',
                display: 'grid',
                gridTemplateColumns: 'repeat(3,1fr)',
                gap: '1px',
                background: 'oklch(0.30 0.022 60)',
              }}
            >
              {[
                { icon: '📞', label: 'Téléphone', value: '04 28 38 21 47' },
                { icon: '✉', label: 'E-mail', value: 'bonjour@cadran.cloud' },
                { icon: '💬', label: 'Chat', value: "Depuis l'app" },
              ].map((item) => (
                <div key={item.label} style={{ padding: '28px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                  <div className="eyebrow" style={{ marginBottom: '6px' }}>
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontFamily: 'Fraunces Variable, serif',
                      fontSize: '17px',
                      color: 'oklch(0.92 0.014 78)',
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
